"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  exportJson,
  getHotspots,
  getSnapshot,
  logSummary,
  type PerfSnapshot,
  type RenderEntry,
  resetStore,
  subscribe,
} from "@/lib/perf/perf-store";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function avg(entry: RenderEntry): number {
  return entry.count > 0 ? entry.totalMs / entry.count : 0;
}

function fmt(ms: number): string {
  return ms.toFixed(2);
}

function severity(entry: RenderEntry): "high" | "medium" | "low" {
  const a = avg(entry);
  if (entry.count >= 20 || a >= 16) return "high";
  if (entry.count >= 10 || a >= 8) return "medium";
  return "low";
}

const SEVERITY_COLOR: Record<"high" | "medium" | "low", string> = {
  high: "#ef4444",
  low: "#22c55e",
  medium: "#f59e0b",
};

// ─── Types ────────────────────────────────────────────────────────────────────

type SortKey = "count" | "avg" | "max" | "unnecessary";
type FilterMode = "all" | "hotspots" | "unnecessary";

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatBadge({
  label,
  value,
  color,
}: {
  label: string;
  value: string | number;
  color?: string;
}) {
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.06)",
        borderRadius: 6,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        padding: "4px 8px",
      }}
    >
      <span
        style={{
          color: "#9ca3af",
          fontSize: 9,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <span style={{ color: color ?? "#f3f4f6", fontSize: 13, fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function SortButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? "rgba(124,111,247,0.25)" : "transparent",
        border: active ? "1px solid rgba(124,111,247,0.5)" : "1px solid rgba(255,255,255,0.1)",
        borderRadius: 4,
        color: active ? "#c4b5fd" : "#9ca3af",
        cursor: "pointer",
        fontSize: 10,
        fontWeight: active ? 600 : 400,
        padding: "2px 6px",
        transition: "all 0.15s",
      }}
      type="button"
    >
      {label}
    </button>
  );
}

function FilterButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? "rgba(255,255,255,0.1)" : "transparent",
        border: "none",
        borderRadius: 4,
        color: active ? "#f3f4f6" : "#6b7280",
        cursor: "pointer",
        fontSize: 10,
        fontWeight: active ? 600 : 400,
        padding: "3px 7px",
        transition: "all 0.15s",
      }}
      type="button"
    >
      {label}
    </button>
  );
}

function EntryRow({ entry }: { entry: RenderEntry }) {
  const sev = severity(entry);
  const color = SEVERITY_COLOR[sev];
  const unnecessaryPct =
    entry.count > 0 ? Math.round((entry.unnecessaryCount / entry.count) * 100) : 0;

  return (
    <div
      style={{
        alignItems: "center",
        borderBottom: "1px solid rgba(255,255,255,0.04)",
        display: "grid",
        gap: 4,
        gridTemplateColumns: "1fr 44px 52px 52px 52px",
        padding: "5px 10px",
      }}
    >
      <div style={{ alignItems: "center", display: "flex", gap: 6, minWidth: 0 }}>
        <span
          style={{
            background: color,
            borderRadius: "50%",
            flexShrink: 0,
            height: 6,
            opacity: 0.85,
            width: 6,
          }}
        />
        <span
          style={{
            color: "#e5e7eb",
            fontFamily: "ui-monospace, monospace",
            fontSize: 11,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={entry.name}
        >
          {entry.name}
        </span>
      </div>
      <span style={{ color, fontSize: 11, fontWeight: 600, textAlign: "right" }}>
        {entry.count}
      </span>
      <span style={{ color: "#9ca3af", fontSize: 10, textAlign: "right" }}>
        {fmt(avg(entry))}ms
      </span>
      <span style={{ color: "#9ca3af", fontSize: 10, textAlign: "right" }}>
        {fmt(entry.maxMs)}ms
      </span>
      <span
        style={{
          color: unnecessaryPct > 50 ? "#ef4444" : unnecessaryPct > 20 ? "#f59e0b" : "#6b7280",
          fontSize: 10,
          textAlign: "right",
        }}
      >
        {unnecessaryPct > 0 ? `${unnecessaryPct}%` : "—"}
      </span>
    </div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

export function PerformancePanel() {
  const [open, setOpen] = useState(false);
  const [snapshot, setSnapshot] = useState<PerfSnapshot | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("count");
  const [filter, setFilter] = useState<FilterMode>("all");
  const [copied, setCopied] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Refresh snapshot whenever the store emits an update.
  useEffect(() => {
    const unsub = subscribe(() => {
      setSnapshot(getSnapshot());
    });
    // Seed initial snapshot.
    setSnapshot(getSnapshot());
    return unsub;
  }, []);

  // Keyboard shortcut: Shift + P
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.shiftKey && e.key === "P") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleReset = useCallback(() => {
    resetStore();
    setSnapshot(getSnapshot());
  }, []);

  const handleCopyJson = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(exportJson());
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      logSummary();
    }
  }, []);

  const handleDownloadJson = useCallback(() => {
    const blob = new Blob([exportJson()], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `perf-report-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const handleLogConsole = useCallback(() => {
    logSummary();
  }, []);

  // Build the displayed rows based on current filter + sort.
  const rows: RenderEntry[] = (() => {
    if (!snapshot) return [];

    let base: RenderEntry[];
    if (filter === "hotspots") {
      base = getHotspots({ minRenders: 5 });
    } else if (filter === "unnecessary") {
      base = getHotspots({ minRenders: 1, unnecessaryOnly: true });
    } else {
      base = [...snapshot.components];
    }

    return base.sort((a, b) => {
      if (sortKey === "count") return b.count - a.count;
      if (sortKey === "avg") return avg(b) - avg(a);
      if (sortKey === "max") return b.maxMs - a.maxMs;
      if (sortKey === "unnecessary") return b.unnecessaryCount - a.unnecessaryCount;
      return 0;
    });
  })();

  const totalRenders = snapshot?.totalRenders ?? 0;
  const elapsedSec = snapshot ? (snapshot.elapsedMs / 1000).toFixed(1) : "0.0";
  const hotspotCount = getHotspots({ minRenders: 5 }).length;
  const unnecessaryCount = getHotspots({
    minRenders: 1,
    unnecessaryOnly: true,
  }).length;

  // Floating toggle button (always visible in dev).
  const toggleButton = (
    <button
      aria-label="Toggle performance panel (Shift+P)"
      onClick={() => setOpen((v) => !v)}
      style={{
        alignItems: "center",
        backdropFilter: "blur(8px)",
        background: open ? "rgba(124,111,247,0.9)" : "rgba(15,15,20,0.88)",
        border: "1px solid rgba(124,111,247,0.4)",
        borderRadius: 8,
        bottom: 68,
        boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
        color: "#f3f4f6",
        cursor: "pointer",
        display: "flex",
        fontSize: 11,
        fontWeight: 600,
        gap: 5,
        padding: "6px 10px",
        position: "fixed",
        right: 24,
        transition: "all 0.2s",
        WebkitBackdropFilter: "blur(8px)",
        zIndex: 99999,
      }}
      title="Toggle performance panel (Shift+P)"
      type="button"
    >
      <span style={{ fontSize: 13 }}>⚡</span>
      <span style={{ fontFamily: "ui-monospace, monospace" }}>
        {totalRenders > 0 ? totalRenders : "Perf"}
      </span>
      {hotspotCount > 0 && (
        <span
          style={{
            background: "#ef4444",
            borderRadius: 10,
            fontSize: 9,
            fontWeight: 700,
            padding: "1px 5px",
          }}
        >
          {hotspotCount}
        </span>
      )}
    </button>
  );

  if (!open) {
    return toggleButton;
  }

  return (
    <>
      {toggleButton}

      {/* Panel */}
      <div
        ref={panelRef}
        style={{
          backdropFilter: "blur(12px)",
          background: "rgba(10,10,15,0.95)",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 12,
          bottom: 108,
          boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
          color: "#f3f4f6",
          display: "flex",
          flexDirection: "column",
          fontFamily: "ui-sans-serif, system-ui, sans-serif",
          fontSize: 12,
          maxHeight: "70vh",
          position: "fixed",
          right: 24,
          WebkitBackdropFilter: "blur(12px)",
          width: 420,
          zIndex: 99998,
        }}
      >
        {/* Header */}
        <div
          style={{
            alignItems: "center",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            gap: 8,
            justifyContent: "space-between",
            padding: "10px 12px",
          }}
        >
          <div style={{ alignItems: "center", display: "flex", gap: 6 }}>
            <span style={{ fontSize: 14 }}>⚡</span>
            <span
              style={{
                color: "#c4b5fd",
                fontWeight: 700,
                letterSpacing: "0.02em",
              }}
            >
              Perf Monitor
            </span>
            <span
              style={{
                background: "rgba(255,255,255,0.07)",
                borderRadius: 4,
                color: "#6b7280",
                fontSize: 9,
                padding: "1px 5px",
              }}
            >
              DEV
            </span>
          </div>

          <div style={{ alignItems: "center", display: "flex", gap: 4 }}>
            <button
              onClick={handleLogConsole}
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 4,
                color: "#9ca3af",
                cursor: "pointer",
                fontSize: 9,
                padding: "2px 6px",
              }}
              title="Log summary to console"
              type="button"
            >
              console
            </button>
            <button
              onClick={handleCopyJson}
              style={{
                background: copied ? "rgba(34,197,94,0.2)" : "transparent",
                border: `1px solid ${copied ? "rgba(34,197,94,0.4)" : "rgba(255,255,255,0.1)"}`,
                borderRadius: 4,
                color: copied ? "#86efac" : "#9ca3af",
                cursor: "pointer",
                fontSize: 9,
                padding: "2px 6px",
                transition: "all 0.2s",
              }}
              title="Copy JSON to clipboard"
              type="button"
            >
              {copied ? "copied ✓" : "copy JSON"}
            </button>
            <button
              onClick={handleDownloadJson}
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 4,
                color: "#9ca3af",
                cursor: "pointer",
                fontSize: 9,
                padding: "2px 6px",
              }}
              title="Download JSON report"
              type="button"
            >
              ↓ JSON
            </button>
            <button
              onClick={handleReset}
              style={{
                background: "transparent",
                border: "1px solid rgba(239,68,68,0.25)",
                borderRadius: 4,
                color: "#f87171",
                cursor: "pointer",
                fontSize: 9,
                padding: "2px 6px",
              }}
              title="Clear all collected data"
              type="button"
            >
              reset
            </button>
          </div>
        </div>

        {/* Stats bar */}
        <div
          style={{
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            display: "flex",
            gap: 6,
            padding: "8px 12px",
          }}
        >
          <StatBadge color="#c4b5fd" label="Total renders" value={totalRenders} />
          <StatBadge label="Elapsed" value={`${elapsedSec}s`} />
          <StatBadge
            color={hotspotCount > 0 ? "#f87171" : "#22c55e"}
            label="Hot spots"
            value={hotspotCount}
          />
          <StatBadge
            color={unnecessaryCount > 0 ? "#f59e0b" : "#22c55e"}
            label="Unnecessary"
            value={unnecessaryCount}
          />
        </div>

        {/* Filter + Sort controls */}
        <div
          style={{
            alignItems: "center",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            display: "flex",
            gap: 8,
            justifyContent: "space-between",
            padding: "6px 12px",
          }}
        >
          <div style={{ alignItems: "center", display: "flex", gap: 3 }}>
            <FilterButton active={filter === "all"} label="All" onClick={() => setFilter("all")} />
            <FilterButton
              active={filter === "hotspots"}
              label={`Hot (${hotspotCount})`}
              onClick={() => setFilter("hotspots")}
            />
            <FilterButton
              active={filter === "unnecessary"}
              label={`Wasteful (${unnecessaryCount})`}
              onClick={() => setFilter("unnecessary")}
            />
          </div>

          <div style={{ alignItems: "center", display: "flex", gap: 3 }}>
            <span style={{ color: "#4b5563", fontSize: 9, marginRight: 2 }}>SORT</span>
            <SortButton
              active={sortKey === "count"}
              label="Count"
              onClick={() => setSortKey("count")}
            />
            <SortButton active={sortKey === "avg"} label="Avg" onClick={() => setSortKey("avg")} />
            <SortButton active={sortKey === "max"} label="Max" onClick={() => setSortKey("max")} />
            <SortButton
              active={sortKey === "unnecessary"}
              label="Waste"
              onClick={() => setSortKey("unnecessary")}
            />
          </div>
        </div>

        {/* Column headers */}
        <div
          style={{
            color: "#4b5563",
            display: "grid",
            fontSize: 9,
            fontWeight: 600,
            gap: 4,
            gridTemplateColumns: "1fr 44px 52px 52px 52px",
            letterSpacing: "0.06em",
            padding: "4px 10px",
            textTransform: "uppercase",
          }}
        >
          <span>Component</span>
          <span style={{ textAlign: "right" }}>×</span>
          <span style={{ textAlign: "right" }}>Avg</span>
          <span style={{ textAlign: "right" }}>Max</span>
          <span style={{ textAlign: "right" }}>Waste</span>
        </div>

        {/* Rows */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {rows.length === 0 ? (
            <div
              style={{
                color: "#4b5563",
                fontSize: 11,
                padding: "24px 12px",
                textAlign: "center",
              }}
            >
              {totalRenders === 0
                ? "No renders recorded yet — interact with the app."
                : "No components match the current filter."}
            </div>
          ) : (
            rows.map((entry) => <EntryRow entry={entry} key={entry.name} />)
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            borderTop: "1px solid rgba(255,255,255,0.06)",
            color: "#4b5563",
            display: "flex",
            fontSize: 9,
            justifyContent: "space-between",
            padding: "6px 12px",
          }}
        >
          <span>
            {rows.length} / {snapshot?.components.length ?? 0} components
          </span>
          <span>
            Press{" "}
            <kbd
              style={{
                background: "rgba(255,255,255,0.08)",
                borderRadius: 3,
                padding: "0 3px",
              }}
            >
              Shift+P
            </kbd>{" "}
            to toggle
          </span>
        </div>
      </div>
    </>
  );
}
